import React, { useState } from 'react';
import { usePromptEngine } from '@/hooks/usePromptEngine';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Loader2, Sparkles, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';

// CSS for wavy red underline
const styles = `
  .conflict-highlight {
    position: relative;
    display: inline-block;
    background: rgba(239, 68, 68, 0.1);
    border-radius: 2px;
    padding: 0 2px;
    margin: 0 1px;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .conflict-highlight:hover {
    background: rgba(239, 68, 68, 0.2);
    transform: translateY(-1px);
  }
  
  .conflict-highlight::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      #ef4444 20%, 
      #ef4444 80%, 
      transparent 100%
    );
    animation: wavy 1.5s ease-in-out infinite;
  }
  
  @keyframes wavy {
    0%, 100% { transform: translateX(0); }
    50% { transform: translateX(2px); }
  }
  
  .success-badge {
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
    animation: pulse 2s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.8; }
  }
  
  .enhanced-text {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
    border-radius: 4px;
    padding: 8px 12px;
    margin: 8px 0;
  }
`;

const PromptEngine: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const {
    tokens,
    conflicts,
    isAnalyzing,
    isEnhancing,
    enhancedPrompt,
    error,
    processInitialPrompt,
    enhancePrompt,
    applyQuickFix,
    getPromptText,
    hasConflicts,
    isConsistent,
    enhancedText,
  } = usePromptEngine();

  const handleProcessPrompt = () => {
    if (inputText.trim()) {
      processInitialPrompt(inputText);
    }
  };

  const handleEnhance = () => {
    enhancePrompt();
  };

  const renderPromptWithHighlights = () => {
    return tokens.map((token) => {
      if (token.hasConflict) {
        const conflict = conflicts?.conflicts.find(c => c.tokenId === token.id);
        return (
          <TooltipProvider key={token.id}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="conflict-highlight">
                  {token.text}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-red-600">
                    {conflict?.conflictType}
                  </p>
                  <p className="text-xs text-gray-600">
                    {conflict?.explanation}
                  </p>
                  {conflict?.suggestion && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => applyQuickFix(token.id)}
                      className="w-full"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Quick Fix
                    </Button>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }
      return <span key={token.id}>{token.text}</span>;
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <style>{styles}</style>
      
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          Smart Prompt Conflict Engine
        </h1>
        <p className="text-gray-600">
          Real-time prompt validation and enhancement powered by Groq AI
        </p>
      </div>

      {/* Input Section */}
      <Card className="p-6 space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Enter your prompt description:</label>
          <div className="flex gap-2">
            <Input
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="e.g., A boy playing ball in the park"
              className="flex-1"
            />
            <Button 
              onClick={handleProcessPrompt}
              disabled={!inputText.trim() || isAnalyzing}
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                'Process'
              )}
            </Button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
      </Card>

      {/* Results Section */}
      {tokens.length > 0 && (
        <Card className="p-6 space-y-4">
          {/* Status Badge */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isConsistent ? (
                <Badge className="success-badge">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Logically Consistent
                </Badge>
              ) : (
                <Badge variant="destructive">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  {hasConflicts ? `${conflicts?.conflicts.length} Conflicts` : 'Inconsistent'}
                </Badge>
              )}
              {isAnalyzing && (
                <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
              )}
            </div>
            
            <Button 
              onClick={handleEnhance}
              disabled={isEnhancing || tokens.length === 0}
              variant="outline"
            >
              {isEnhancing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enhancing...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Enhance
                </>
              )}
            </Button>
          </div>

          {/* Prompt Display */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Processed Prompt:</label>
            <div className="p-4 bg-gray-50 rounded-md border min-h-[60px]">
              <div className="text-base leading-relaxed">
                {renderPromptWithHighlights()}
              </div>
            </div>
          </div>

          {/* Enhanced Prompt */}
          {enhancedPrompt && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Enhanced Prompt:</label>
              <div className="enhanced-text">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4" />
                  <span className="font-medium">Cinematic 8k Enhancement</span>
                </div>
                <p className="text-sm">{enhancedText}</p>
              </div>
            </div>
          )}

          {/* Token Details */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Token Analysis:</label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {tokens.map((token) => (
                <div
                  key={token.id}
                  className={`p-2 rounded border text-xs ${
                    token.hasConflict 
                      ? 'border-red-200 bg-red-50' 
                      : 'border-green-200 bg-green-50'
                  }`}
                >
                  <div className="font-medium">{token.text}</div>
                  <div className="text-gray-500">{token.category}</div>
                  {token.hasConflict && (
                    <div className="text-red-600 mt-1">
                      Conflict detected
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Conflicts Summary */}
          {conflicts && conflicts.conflicts.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Conflicts Found:</label>
              <div className="space-y-2">
                {conflicts.conflicts.map((conflict, index) => (
                  <div key={index} className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-red-800">
                          {conflict.conflictType}
                        </div>
                        <div className="text-sm text-red-600">
                          {conflict.explanation}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          Suggestion: {conflict.suggestion}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => applyQuickFix(conflict.tokenId)}
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Fix
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default PromptEngine;
